// API client and base URL. Imports startSubscriptionRedirect lazily (it lives
// in modals/paywall.js and itself calls into this module for API_BASE — the two
// form a small mutual-dependency that ESM handles via live bindings as long as
// neither calls the other at module-load time).
import { startSubscriptionRedirect } from "../modals/paywall.js";
import { toPence } from "../../../shared/money.js";

const IS_LOCALHOST =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const IS_SENDINVO_DOMAIN =
  window.location.hostname === "sendinvo.com" || window.location.hostname.endsWith(".sendinvo.com");

// Local dev serves UI on :8080 and API on :3000. Hosted deploys call the API service directly.
/** @type {string} Origin (no trailing slash) of the API. */
export const API_BASE = IS_LOCALHOST
  ? `${window.location.protocol}//${window.location.hostname}:3000`
  : IS_SENDINVO_DOMAIN
    ? "https://api.sendinvo.com"
    : "https://ar-ap-api-frankfurt.onrender.com";

/**
 * @typedef {object} ApiOptions
 * @property {string}                 [method]      HTTP method (default "GET", or "POST" if body present)
 * @property {BodyInit|object|null}   [body]        Object-as-body will be JSON-stringified by the caller; we set content-type only when it isn't already
 * @property {Record<string,string>}  [headers]     Extra headers; content-type defaults to application/json when body is present and not FormData
 * @property {AbortSignal}            [signal]
 */

/**
 * Wrapped fetch that prefixes API_BASE, always includes credentials, JSON-parses
 * responses, throws on non-2xx, and intercepts 402 SubscriptionRequired to send
 * the user straight to the Stripe billing flow.
 *
 * @param {string}     path            API path beginning with "/"
 * @param {ApiOptions} [options]
 * @returns {Promise<any>} parsed JSON body, or null for 204 No Content
 * @throws  {Error} when the response is not ok (message comes from body.message
 *                  / body.error / fallback "Request failed (status).")
 */
export async function api(path, options = {}) {
  options = { ...options, body: onTheWire(options.method, path, options.body) };
  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    ...(options.headers || {})
  };
  if (hasBody && !isFormData && !headers["content-type"] && !headers["Content-Type"]) {
    headers["content-type"] = "application/json";
  }
  // One key per call, and a call is one click: a request that emails a client
  // or moves money can then only happen once however the network treats it
  // (server/idempotency.js).
  if (needsIdempotencyKey(options.method, path) && !headers["Idempotency-Key"]) {
    headers["Idempotency-Key"] = newIdempotencyKey();
  }

  // headers goes last: spreading options after it replaced the merged headers
  // with the caller's own, dropping content-type from any request that passed
  // a header of its own.
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers
  });

  if (res.status === 204) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 402 SubscriptionRequired → send the user straight to the Stripe billing
    // flow instead of surfacing the raw error. The send/create endpoints
    // return this shape when the company has no active Sendinvo subscription.
    if (res.status === 402 && body && body.error === "SubscriptionRequired") {
      try {
        startSubscriptionRedirect({ status: body.subscriptionStatus || "none" });
      } catch {
        // If the redirect fails to start for any reason, fall through to the error.
      }
    }
    const err = new Error(body.message || body.error || `Request failed (${res.status}).`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

/**
 * The If-Match header for a write based on a row's version (server/row-version.js),
 * or no header when there isn't one — the demo's rows carry none.
 *
 * @param {number|null|undefined} version
 * @returns {Record<string,string>}
 */
export function ifMatch(version) {
  return Number.isInteger(version) ? { "If-Match": `"${version}"` } : {};
}

// The requests that send a document or move money: the ones the server keys
// (KEYED_ROUTES in server/idempotency.js). Paths only, so the query string of
// an id can't matter.
const KEYED_PATH = /^\/(invoices|quotes|credit-notes)\/[^/?#]+\/(send|resend|schedule-send|record-payment|record-refund|mark-paid|refund|write-off|void)(?:[?#]|$)/;

function needsIdempotencyKey(method, path) {
  return String(method || "GET").toUpperCase() === "POST" && KEYED_PATH.test(String(path || ""));
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ── The request body's wire shape (iOS map, ticket 06, answers 4 and 6) ────
// Every amount also goes as integer pence, named …Pence, beside the pounds
// fields — the server prefers pence (server/money-input.js), and an API or demo
// that predates it still reads pounds. And a new document or client names its
// own id (server/client-id.js). Done here, once, so no caller can miss it; the
// pounds fields go when every client sends pence.

const DOCUMENT_BODY = /^\/(invoices\/draft|invoices\/[^/?#]+|quotes\/draft|quotes\/[^/?#]+)(?:[?#]|$)/;
const ITEMS_BODY = /^\/credit-notes(?:[?#]|$)/;
const SCHEDULE_BODY = /^\/schedules(?:\/[^/?#]+)?(?:[?#]|$)/;
const AMOUNT_BODY = /^\/invoices\/[^/?#]+\/(record-payment|record-refund)(?:[?#]|$)/;
const CREATES = /^\/(invoices\/draft|quotes\/draft|credit-notes|schedules|contacts)(?:[?#]|$)/;

function penceOf(value) {
  if (value === undefined || value === null || String(value).trim() === "") return undefined;
  const n = Number.parseFloat(String(value));
  return Number.isFinite(n) && n >= 0 ? toPence(n) : undefined;
}

function addPence(target, poundsField, penceField) {
  if (!target || typeof target !== "object" || penceField in target) return;
  const pence = penceOf(target[poundsField]);
  if (pence !== undefined) target[penceField] = pence;
}

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function onTheWire(method, path, body) {
  const verb = String(method || "GET").toUpperCase();
  if ((verb !== "POST" && verb !== "PUT") || typeof body !== "string") return body;
  const route = String(path || "");
  // Anchored at the end, so only a draft create or edit: /invoices/:id/send
  // and every other action under a document doesn't match.
  const document = verb === "POST" && DOCUMENT_BODY.test(route);
  const items = verb === "POST" && ITEMS_BODY.test(route);
  const schedule = SCHEDULE_BODY.test(route) && (verb === "POST" ? !/\/schedules\/[^/?#]+/.test(route) : true);
  const amount = verb === "POST" && AMOUNT_BODY.test(route);
  const create = verb === "POST" && CREATES.test(route);
  if (!document && !items && !schedule && !amount && !create) return body;

  let parsed;
  try { parsed = JSON.parse(body); } catch { return body; }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return body;

  if (document || items) {
    (Array.isArray(parsed.items) ? parsed.items : []).forEach((item) => addPence(item, "unitPrice", "unitPricePence"));
  }
  if (document) {
    addPence(parsed, "cisLabourAmount", "cisLabourAmountPence");
    if (String(parsed.discountType || "").toLowerCase() === "fixed") addPence(parsed, "discountValue", "discountValuePence");
    if (String(parsed.depositType || "").toLowerCase() === "fixed") addPence(parsed, "depositValue", "depositValuePence");
  }
  if (schedule) {
    (Array.isArray(parsed.lineItems) ? parsed.lineItems : []).forEach((item) => addPence(item, "unitPrice", "unitPricePence"));
  }
  if (amount) addPence(parsed, "amount", "amountPence");
  if (create && !parsed.id) parsed.id = newId();
  return JSON.stringify(parsed);
}
